import type { WorkPlan } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { appWeekdayDayMonth } from "./app-dates.ts";
import { factKeys, type StoredConversationEvent } from "./conversation-events.ts";
import type {
  ForcedDraftRecord,
  PlainRedraftRecord,
  RedraftLiftRecord,
} from "./draft-force-records.ts";
import { draftingOffPhrase, isDeveloperReviewStep, visibleSteps } from "./inbox-view.ts";
import type { RepReplyActivity } from "./rep-reply.ts";

/*
 * The Activity tab as a pure projection (docs/2026-09-19-inbox-direction-a-design.md, section 3.3):
 * events and the plan in, rows out. The browser renders these rows and the tests assert them, so
 * what a rep reads is decided here and nowhere else. The clock and time zone are inputs.
 */

export const ACTIVITY_FILTERS = ["all", "mail", "you", "agent", "providers"] as const;
export type ActivityFilter = (typeof ACTIVITY_FILTERS)[number];

export const activityFilterLabel: Record<ActivityFilter, string> = {
  all: "All",
  mail: "Mail",
  you: "You",
  agent: "Agent",
  providers: "Providers",
};

/** Who acted, as the dot and the chip show it. */
type ActivityTone = "mail" | "you" | "rep" | "agent" | "provider" | "system";

type ActivityEvidence =
  | { kind: "message"; messageId: string }
  | { kind: "draft"; revision: number }
  | { kind: "receipt"; ref: string }
  /** The rep's own reply as reported, from its own table (compose view phase 1). */
  | { kind: "reply_text"; html: string };

export type ActivityEntry = {
  key: string;
  at: string;
  time: string;
  tone: ActivityTone;
  /** The verb, set in weight, then the rest of the sentence. */
  lead: string;
  rest: string;
  who: string;
  evidence: ActivityEvidence | null;
};

export type ActivityRow =
  | { type: "day"; key: string; label: string }
  | ({ type: "event" } & ActivityEntry)
  | { type: "system"; key: string; entries: ActivityEntry[] }
  | { type: "next"; key: string; steps: string[] };

export type ActivityView = {
  rows: ActivityRow[];
  counts: Record<ActivityFilter, number>;
};

const stanceLabel: Record<string, string> = {
  interested_engaged: "Interested",
  brushoff_soft_yes: "Soft yes, materials only",
  curiosity_no_intent: "Curious, no intent yet",
  timing_defer: "Not now, later",
  not_interested_soft: "Not interested",
  not_interested_hard: "Firm no",
  opt_out_request: "Asked to opt out",
  hostile_complaint: "Complaint",
  legal_privacy_demand: "Privacy or legal demand",
  auto_reply_ooo: "Out-of-office reply",
  auto_reply_other: "Automatic reply",
  role_change_departure_auto_notice: "Left the company (auto notice)",
  unrelated_correspondence: "Unrelated mail",
  reverse_pitch_partnership: "Pitched back",
  not_a_lead_reply: "Not a reply from the lead",
  other: "Other",
};

const askLabel: Record<string, string> = {
  send_materials: "materials",
  answer_pricing: "pricing",
  confirm_scope: "scope",
  route_to_person: "the right person",
  propose_times: "times",
  answer_data_source: "where the data came from",
  remove_me: "removal",
  stop_until_date: "a pause",
  add_attendees: "more attendees",
  answer_technical: "a technical answer",
  confirm_meeting_details: "meeting details",
  provide_references: "references",
  reschedule: "a new time",
  sign_nda: "an NDA",
  route_to_procurement: "procurement",
  confirm_their_time: "confirming their time",
  register_attendee: "registration",
  other: "something else",
};

const dispositionLabel: Record<string, string> = {
  active: "Back in progress",
  qualification_pending: "Held · awaiting qualification",
  qualification_error: "Held · qualification error",
  ignored_not_qualified: "Closed · not qualified",
  drafting_off: "Held · drafting off",
  back_drafting_disabled: "Held · imported-history drafting unavailable",
  backfill_not_drafted: "Held · older than the backfill window",
  sender_only_cc: "Closed · addressed to someone else",
  answered_by_seller: "Answered by the seller in Bison",
  meeting_already_confirmed: "Held · meeting already booked",
  continued_not_drafted: "Held · wrote again outside the campaign",
};

/** The pipeline's check phases, named for what they look at rather than for the stage. */
const checkPhaseLabel: Record<string, string> = {
  context: "context check",
  draft: "rule check",
  judge: "grounding review",
};

const providerChangeLabel: Record<string, string> = {
  seller_answered: "the seller had already answered in Bison",
  trigger_changed: "the lead's reply changed in Bison",
  newer_inbound: "a newer reply arrived",
  conversation_changed: "the conversation changed in Bison",
};

function humanList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function tone(event: StoredConversationEvent, viewerId: string | null): ActivityTone {
  if (event.kind === "message.inbound" || event.kind === "message.outbound") return "mail";
  switch (event.actor.type) {
    case "rep":
      return event.actor.id !== null && event.actor.id === viewerId ? "you" : "rep";
    case "agent":
      return "agent";
    case "provider":
    case "external":
      return "provider";
    case "lead":
    case "seller":
      return "mail";
    case "system":
      return "system";
  }
}

function who(event: StoredConversationEvent, itsTone: ActivityTone): string {
  switch (itsTone) {
    case "mail":
      return "Bison";
    case "you":
      return "you";
    case "rep":
      return "a rep";
    case "agent":
      return "agent";
    case "provider":
      return event.actor.id === "hubspot"
        ? "HubSpot"
        : event.actor.type === "external"
          ? "external"
          : "Bison";
    case "system":
      return "system";
  }
}

function evidence(event: StoredConversationEvent): ActivityEvidence | null {
  if (event.evidence.kind === "bison_message")
    return { kind: "message", messageId: event.evidence.ref };
  if (event.evidence.kind === "draft_revision") {
    const revision = Number(/:r(\d+)$/.exec(event.evidence.ref)?.[1]);
    return Number.isInteger(revision) ? { kind: "draft", revision } : null;
  }
  if (event.evidence.kind === "provider_receipt")
    return { kind: "receipt", ref: event.evidence.ref };
  return null;
}

/** One plain sentence per event: a verb to set in weight, then the rest. */
/** One item on the timeline: an event from the shared record, or a forced draft from its own table. */
type TimelineItem = { at: string; order: number } & (
  | { event: StoredConversationEvent; record: null; reply: null }
  | { event: null; record: ForcedDraftRecord | RedraftLiftRecord | PlainRedraftRecord; reply: null }
  | { event: null; record: null; reply: RepReplyActivity }
);

/**
 * A line of the rep's own reply (compose view phase 1), in the same voice as the events: who
 * replied, and what became of the report. Sight in Bison is Bison's, not the rep's.
 */
function describeRepReply(line: RepReplyActivity, you: boolean): { lead: string; rest: string } {
  const subject = you ? "You" : "A rep";
  switch (line.kind) {
    case "reported":
      return { lead: `${subject} wrote a reply`, rest: "and reported it sent in Bison" };
    case "seen":
      return { lead: "Seen in Bison", rest: ": the reply is in the thread" };
    case "withdrawn":
      return { lead: `${subject} took back`, rest: "the report: that reply was not sent" };
    case "discarded":
      return { lead: `${subject} discarded`, rest: "a reply before it went out" };
  }
}

/**
 * A forced draft, in the same plain voice as the events around it. The hold is named rather than the
 * mechanism: what a rep wants to know is that this conversation was drafted although drafting would
 * not have picked it up, and why someone asked for it.
 */
function describeForcedDraft(
  record: ForcedDraftRecord | RedraftLiftRecord,
  you: boolean,
): { lead: string; rest: string } {
  return {
    lead: `${you ? "You" : "A rep"} asked for a draft`,
    rest: `although ${
      record.lifted === "drafting_off"
        ? "drafting was switched off for it"
        : record.lifted === "auto_reply_stop"
          ? "the saved classifier marked this turn as an automatic reply"
          : record.lifted === "outside_goal_stop"
            ? "the app had read this reply as not a sales reply"
            : record.lifted === "lead_link_stop"
              ? "the lead offered their own booking page; this reply sends ours"
              : record.lifted === "conditional_no_stop"
                ? "the lead said no unless a condition held; the rep confirmed it holds"
                : "this reply came in as history"
    }: ${record.reason}`,
  };
}

/**
 * A rep's "Draft again": the reply was already the app's to draft, and a rep asked for the draft
 * again from the open conversation. The sentence is this view's own. Nothing of the record is
 * printed: its reason and its actor are stored text, and the same table holds an operator's.
 */
function describePlainRedraft(you: boolean): { lead: string; rest: string } {
  return { lead: `${you ? "You" : "A rep"} asked for a new draft`, rest: "with Draft again" };
}

type DraftRecord = ForcedDraftRecord | RedraftLiftRecord | PlainRedraftRecord;

/** The records from their own tables as timeline items; an absent list is an older server's. */
function recordItems(...lists: (readonly DraftRecord[] | undefined)[]): TimelineItem[] {
  return lists.flatMap((list) =>
    (list ?? []).map((record) => ({
      at: record.requestedAt,
      order: record.providerEventId,
      event: null,
      record,
      reply: null,
    })),
  );
}

/**
 * A record's row: its key, its sentence and who asked. A lift keeps the key and the name it has
 * always had.
 */
function recordEntry(
  record: DraftRecord,
  you: boolean,
): { key: string; lead: string; rest: string; who: string } {
  const attempt = "attempt" in record ? `:attempt:${record.attempt}` : "";
  if ("lifted" in record)
    return {
      key: `forced:${record.providerEventId}${attempt}`,
      ...describeForcedDraft(record, you),
      who: you ? "you" : record.actorId,
    };
  return {
    key: `redraft:${record.providerEventId}${attempt}`,
    ...describePlainRedraft(you),
    who: you ? "you" : "a rep",
  };
}

const outcomeKeys = (decisions: readonly string[] = []): ReadonlySet<string> =>
  new Set(decisions.map(factKeys.outcome));

/**
 * An outcome a Done recorded says so (LEA-225): marking it done is what closed the draft. Its event
 * key carries the decision (`factKeys.outcome`), which the Done's closure row names.
 */
function describeWithDone(
  event: StoredConversationEvent,
  you: boolean,
  doneKeys: ReadonlySet<string>,
): { lead: string; rest: string } {
  if (event.kind === "outcome.reviewed" && doneKeys.has(event.eventKey))
    return { lead: `${you ? "You" : "A rep"} marked it done`, rest: "and the draft closed" };
  return describe(event, you);
}

function describe(event: StoredConversationEvent, you: boolean): { lead: string; rest: string } {
  const subject = you ? "You" : "A rep";
  switch (event.kind) {
    case "message.inbound":
      return { lead: "Reply", rest: "from the lead" };
    case "message.outbound":
      return { lead: "Sent", rest: "from the campaign mailbox" };
    case "reply.classified": {
      const asks = event.detail.asks.map((ask) => askLabel[ask] ?? ask.replaceAll("_", " "));
      return {
        lead: "Classified",
        rest: `${stanceLabel[event.detail.stance] ?? event.detail.stance.replaceAll("_", " ")}${
          asks.length ? ` · asks for ${humanList(asks)}` : ""
        }`,
      };
    }
    case "question.asked":
      return {
        lead: "Question asked",
        rest: `: ${humanList(event.detail.keys.map((key) => key.replaceAll("_", " ")))}`,
      };
    case "question.answered":
      return {
        lead: `${subject} answered`,
        rest: event.detail.uploadIds.length
          ? `with ${plural(event.detail.uploadIds.length, "file", "files")}`
          : "the question",
      };
    case "promise.created":
      return {
        lead: `${subject} promised to follow up`,
        rest: `about ${event.detail.topic} by ${event.detail.followUpOn}`,
      };
    case "promise.answer_ready":
      return { lead: "Promised answer ready", rest: "for the follow-up reply" };
    case "promise.kept":
      return { lead: "Promise kept", rest: "in the reported reply" };
    case "draft.checked": {
      const [phase = "", verdict] = event.detail.verdict.split(":");
      const check = checkPhaseLabel[phase] ?? "check";
      return {
        lead: `Draft ${event.detail.revision + 1} checked`,
        rest:
          verdict === "passed"
            ? `· ${check} passed`
            : `· ${check} flagged ${plural(event.detail.ruleIds.length, "rule", "rules")}`,
      };
    }
    case "draft.generated":
      return { lead: `Draft ${event.detail.draftRevision + 1}`, rest: "written" };
    case "draft.withheld":
      return {
        lead: "Draft withheld",
        rest: event.detail.ruleIds.length
          ? `for review: ${plural(event.detail.ruleIds.length, "check", "checks")} blocked it`
          : "for review",
      };
    case "draft.edited":
      return {
        lead: `${subject} edited`,
        rest: `the draft (version ${event.detail.draftRevision + 1})`,
      };
    case "step.approved":
      return {
        lead: `${subject} approved`,
        rest:
          event.detail.draftRevision === null
            ? "the action"
            : `draft version ${event.detail.draftRevision + 1}`,
      };
    case "step.skipped":
      return { lead: `${subject} skipped`, rest: "a step" };
    case "step.revised":
      return { lead: "Step revised", rest: "" };
    case "step.queued":
      return { lead: `${subject} started`, rest: "the approved action" };
    case "step.reopened":
      return { lead: "Reopened", rest: "a step" };
    case "step.completed":
      return {
        lead: event.detail.evidence === "rep_claim" ? `${subject} marked done` : "Step completed",
        rest: event.detail.evidence === "clock" ? "when its wait ended" : "",
      };
    case "step.invalidated":
      return {
        lead: "Step set aside",
        rest:
          event.detail.cause === "held"
            ? "because the conversation is held"
            : event.detail.cause === "superseded"
              ? "for a newer proposal"
              : `(${event.detail.cause.replaceAll("_", " ")})`,
      };
    case "approval.refused_stale":
      return {
        lead: "Approval refused",
        rest: `: ${providerChangeLabel[event.detail.observed] ?? event.detail.observed}`,
      };
    case "reply.reported_sent":
      // Sent by the app on approval, proven by Bison's receipt; otherwise a rep's own report.
      if (event.actor.type === "provider")
        return {
          lead: "Sent",
          rest: `draft version ${event.detail.draftRevision + 1} through Bison`,
        };
      return {
        lead: `${subject} reported sent`,
        rest: `draft version ${event.detail.draftRevision + 1} from Bison`,
      };
    case "reply.sent_observed":
      return { lead: "Seen in Bison", rest: "the reply was sent" };
    case "effect.started":
      return { lead: "Action started", rest: "" };
    case "effect.executed":
      return { lead: "Action done", rest: "in Bison" };
    case "effect.uncertain":
      return { lead: "Action unconfirmed", rest: "check Bison before retrying" };
    case "effect.cancelled":
      return { lead: "Action cancelled", rest: "before it ran" };
    case "suppression.recorded":
      return { lead: "Opt-out recorded", rest: "no further drafts for this lead" };
    case "goal.completed":
      return event.detail.source === "rep_claim"
        ? { lead: `${subject} marked booked`, rest: "" }
        : { lead: "Meeting booked", rest: "matched in HubSpot" };
    case "outcome.reviewed":
      if (event.detail.kind === "active")
        return { lead: `${subject} reopened the draft`, rest: "" };
      return {
        lead: `${subject} closed`,
        rest: event.detail.kind === "no_response" ? "with no reply needed" : "as deck only",
      };
    case "notification.sent":
      return {
        lead: "Notified",
        rest: `on ${event.detail.channel === "telegram" ? "Telegram" : event.detail.channel}`,
      };
    case "qualification.decided":
      return {
        lead:
          event.detail.outcome === "qualified"
            ? "Qualified"
            : event.detail.outcome === "not_qualified"
              ? "Not qualified"
              : "Qualification failed",
        rest:
          event.actor.type === "rep"
            ? you
              ? "by you"
              : "by a rep"
            : "by the qualification service",
      };
    case "disposition.set":
      return {
        lead:
          event.detail.disposition === "drafting_off" &&
          (event.detail.reason === "workspace" ||
            event.detail.reason === "campaign" ||
            event.detail.reason === "lead")
            ? `Held · ${draftingOffPhrase(event.detail.reason).toLowerCase()}`
            : (dispositionLabel[event.detail.disposition] ?? event.detail.disposition),
        rest: "",
      };
    case "correction":
      return event.detail.supersedes.startsWith("goal:")
        ? { lead: `${subject} undid`, rest: "the booking mark" }
        : { lead: "Corrected", rest: "an earlier entry" };
  }
}

function matches(filter: ActivityFilter, itsTone: ActivityTone): boolean {
  switch (filter) {
    case "all":
      return true;
    case "mail":
      return itsTone === "mail";
    case "you":
      return itsTone === "you" || itsTone === "rep";
    case "agent":
      return itsTone === "agent";
    case "providers":
      return itsTone === "provider";
  }
}

/** Steps not yet finished, in plan order: what happens next, taken from the plan, not from events. */
/** The timeline's "next" row: the rep's open steps. A stop that belongs to Bison is never one. */
/** What is still to do, in the rep's words: a withheld draft is the card's own title, never a review. */
function pending(plan: WorkPlan): string[] {
  return visibleSteps(plan)
    .filter((step) => !["completed", "reported", "skipped", "invalidated"].includes(step.state))
    .map((step) => (isDeveloperReviewStep(step) ? "No draft for this reply" : step.title));
}

export function activityView(input: {
  events: StoredConversationEvent[];
  plan: WorkPlan;
  filter: ActivityFilter;
  viewerId: string | null;
  timeZone: string;
  now: Date;
  /*
   * Forced drafts live in their own table, not in the event vocabulary, so that a rollback cannot
   * meet a kind it does not know (migration 046). They are merged here by time, which is the only
   * place the two streams need to know about each other.
   */
  forcedDrafts?: readonly ForcedDraftRecord[];
  redraftLifts?: readonly RedraftLiftRecord[];
  /** Redrafts that lifted nothing, from the same table as the lifts and beside them on the wire. */
  plainRedrafts?: readonly PlainRedraftRecord[];
  /** The rep's own replies, from their own tables for the same reason (migration 059). */
  repReplies?: readonly RepReplyActivity[];
  /** The reviewed outcomes a Done made, from their own table (migration 156). */
  doneDecisions?: readonly string[];
}): ActivityView {
  const doneKeys = outcomeKeys(input.doneDecisions);
  // The heading's words come from app-dates.ts, so the weekday and the month read the same here as
  // in the list and in the thread, and the same in every runtime. Until now this asked Intl for the
  // month name and then rewrote "Sept" to "Sep" afterwards, which fixed one runtime's spelling of
  // one month and left every other name to the runtime. The two formatters below stay: they write
  // numbers only, which no CLDR disagrees about.
  const dayHeading = (at: Date): string =>
    appWeekdayDayMonth(at.toISOString(), { timeZone: input.timeZone });
  const dayKey = new Intl.DateTimeFormat("en-CA", {
    timeZone: input.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: input.timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const today = dayKey.format(input.now);
  const yesterday = dayKey.format(new Date(input.now.getTime() - 86_400_000));
  const ordered: TimelineItem[] = [
    ...input.events.map((event) => ({
      at: event.occurredAt,
      order: event.id,
      event,
      record: null,
      reply: null,
    })),
    ...recordItems(input.forcedDrafts, input.redraftLifts, input.plainRedrafts),
    ...(input.repReplies ?? []).map((reply, index) => ({
      at: reply.at,
      order: index,
      event: null,
      record: null,
      reply,
    })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.order - b.order);
  const counts: Record<ActivityFilter, number> = {
    all: 0,
    mail: 0,
    you: 0,
    agent: 0,
    providers: 0,
  };
  const rows: ActivityRow[] = [];
  let currentDay = "";
  for (const item of ordered) {
    const itsTone: ActivityTone = item.event
      ? tone(item.event, input.viewerId)
      : item.reply
        ? item.reply.kind === "seen"
          ? "provider"
          : item.reply.actorId === input.viewerId
            ? "you"
            : "rep"
        : item.record.actorId === input.viewerId
          ? "you"
          : "rep";
    for (const filter of ACTIVITY_FILTERS) if (matches(filter, itsTone)) counts[filter] += 1;
    if (!matches(input.filter, itsTone)) continue;
    const at = new Date(item.at);
    const eventDay = dayKey.format(at);
    if (eventDay !== currentDay) {
      currentDay = eventDay;
      rows.push({
        type: "day",
        key: `day:${eventDay}`,
        label: eventDay === today ? "Today" : eventDay === yesterday ? "Yesterday" : dayHeading(at),
      });
    }
    const entry: ActivityEntry = item.reply
      ? {
          key: `reply:${item.reply.repReplyId}:${item.reply.kind}:${item.reply.at}`,
          at: item.at,
          time: clock.format(at),
          tone: itsTone,
          ...describeRepReply(item.reply, itsTone === "you"),
          who: itsTone === "provider" ? "Bison" : itsTone === "you" ? "you" : item.reply.actorId,
          evidence: item.reply.email ? { kind: "reply_text", html: item.reply.email.html } : null,
        }
      : item.event
        ? {
            key: `event:${item.event.id}`,
            at: item.at,
            time: clock.format(at),
            tone: itsTone,
            ...describeWithDone(item.event, itsTone === "you", doneKeys),
            who: who(item.event, itsTone),
            evidence: evidence(item.event),
          }
        : {
            at: item.at,
            time: clock.format(at),
            tone: itsTone,
            ...recordEntry(item.record, itsTone === "you"),
            evidence: null,
          };
    const last = rows.at(-1);
    if (itsTone === "system" && input.filter === "all") {
      // Housekeeping collapses into one row between its neighbours; the rep can open it.
      if (last?.type === "system") last.entries.push(entry);
      else rows.push({ type: "system", key: `system:${entry.key}`, entries: [entry] });
      continue;
    }
    rows.push({ type: "event", ...entry });
  }
  const next = pending(input.plan);
  if (next.length && (input.filter === "all" || input.filter === "you"))
    rows.push({ type: "next", key: "next", steps: next });
  return { rows, counts };
}
